import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import '../style/marketplaceDescription.css';

import calendarIcon from '../../../assets/icons/calendar.svg';
import heartIcon from '../../../assets/icons/heart.svg';
import shareIcon from '../../../assets/icons/share.svg';
import activeHeartIcon from '../../../assets/icons/bottomBar/active_heart.svg';
import starIcon from '../../../assets/icons/star.svg';
import { addToFavorites, removeFromFavorites } from "../../../api/favoritecall";
import { t } from 'i18next';
import { formatPrice } from '../../../utils/formatPrice';
import { trackButtonClick } from '../../../utils/AnalyticsUtils';

// Inline SVG icons
const StockIcon = () => (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M20 7H4V5h16v2zm0 2H4v11h16V9zm-18-4v15h20V5H2zm3 8h2v2H5v-2zm4 0h2v2H9v-2zm4 0h2v2h-2v-2z" fill="currentColor"/>
    </svg>
);

const DeliveryIcon = () => (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M20 8h-3V4H3v16h2v-6h11v6h2v-5l3 3v-6c0-1.1-.9-2-2-2zm-2 6v-2h1v2h-1zM5 14V6h10v8H5z" fill="currentColor"/>
    </svg>
);

const MinOrderIcon = () => (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M11 9h2V7h-2v2zm0-4h2V3h-2v2zm0 8h2v-2h-2v2zm0 4h2v-2h-2v2zM3 13h2v-2H3v2zm0 4h2v-2H3v2zm0-8h2V7H3v2zm0-4h2V3H3v2zm16 8h2v-2h-2v2zm0 4h2v-2h-2v2zm0-8h2V7h-2v2zm0-4h2V3h-2v2zm-4 18H7v-2h8v2z" fill="currentColor"/>
    </svg>
);

const QRIcon = () => (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
        <rect x="3" y="3" width="7" height="7" rx="1" stroke="currentColor" strokeWidth="2"/>
        <rect x="14" y="3" width="7" height="7" rx="1" stroke="currentColor" strokeWidth="2"/>
        <rect x="3" y="14" width="7" height="7" rx="1" stroke="currentColor" strokeWidth="2"/>
        <rect x="14" y="14" width="7" height="7" rx="1" stroke="currentColor" strokeWidth="2"/>
        <path d="M6 6h1v1H6V6zm11 0h1v1h-1V6zm-11 11h1v1H6v-1zm11 0h1v1h-1v-1z" fill="currentColor"/>
    </svg>
);

const MarketplaceDescription = ({
    id,
    title,
    date,
    price,
    price_type,
    is_negotiable,
    barter,
    description,
    rating,
    reviews,
    user_id,
    is_favorite = false,
    stock_quantity,
    price_min,
    min_order_quantity,
    can_deliver,
    currency = 'KZT',
}) => {
    const navigate = useNavigate();
    const [isFavorite, setIsFavorite] = useState(is_favorite);
    const [showQRModal, setShowQRModal] = useState(false);

    useEffect(() => {
        setIsFavorite(is_favorite);
    }, [is_favorite]);

    const handleFavoriteToggle = async (e) => {
        e.stopPropagation();
        e.preventDefault();

        const accessToken = localStorage.getItem("access_token");

        if (!accessToken) {
            navigate('/sign');
            return;
        }

        try {
            if (isFavorite) {
                await removeFromFavorites(id);
                trackButtonClick('remove_favorite', { announcement_id: id });
                setIsFavorite(false);
            } else {
                await addToFavorites(id);
                trackButtonClick('add_favorite', { announcement_id: id });
                setIsFavorite(true);
            }
        } catch (error) {
            console.error("Error toggling favorite:", error);
            setIsFavorite(!isFavorite);
        }
    };

    const handleShare = async () => {
        if (navigator.share) {
            try {
                await navigator.share({
                    title: title,
                    text: description,
                    url: window.location.href,
                });
                trackButtonClick('share_content', { announcement_id: id });
            } catch (error) {
                console.error('Error sharing content:', error);
            }
        } else {
            alert('Sharing is not supported in this browser.');
        }
    };

    const handleBuyClick = () => {
        trackButtonClick('marketplace_buy_click', { announcement_id: id });
        setShowQRModal(true);
    };

    const handleCloseQRModal = () => {
        setShowQRModal(false);
    };

    const formattedDate = new Date(date).toLocaleDateString(t('locale'));

    const appStoreUrl = 'https://apps.apple.com/kz/app/agroland';
    const playStoreUrl = 'https://play.google.com/store/apps/details?id=kz.agroland.app';
    const deepLink = `agroland://marketplace/announcement/${id}`;

    return (
        <div className="marketplace_description_content">
            {/* HEADER */}
            <div className="description_header">
                <div className="announcement_date_content">
                    <img src={calendarIcon} alt="Calendar Icon" />
                    <p className="announcement_date">
                        {t('publishedAt')} {formattedDate}
                    </p>
                </div>
                <div className="action_icons">
                    <img
                        src={shareIcon}
                        alt="share"
                        className="favorite_icon click"
                        onClick={handleShare}
                    />
                    <img
                        key={isFavorite ? "favorite" : "not-favorite"}
                        src={isFavorite ? activeHeartIcon : heartIcon}
                        alt={isFavorite ? "Remove from favorites" : "Add to favorites"}
                        className="favorite_icon click"
                        onClick={handleFavoriteToggle}
                    />
                </div>
            </div>

            {/* MARKETPLACE BADGE */}
            <div className="marketplace_badge">
                <span>{t('marketplace')}</span>
            </div>

            {/* TITLE */}
            <h2 className="announcement_title">
                {title || 'Без названия'}
            </h2>

            {/* STOCK INFO */}
            <div className="marketplace_info_section">
                <div className="info_item">
                    <StockIcon />
                    <div className="info_content">
                        <span className="info_label">{t('inStock')}</span>
                        <span className="info_value">{stock_quantity} {t('units')}</span>
                    </div>
                </div>

                {min_order_quantity > 0 && (
                    <div className="info_item">
                        <MinOrderIcon />
                        <div className="info_content">
                            <span className="info_label">{t('minOrder')}</span>
                            <span className="info_value">{min_order_quantity} {t('units')}</span>
                        </div>
                    </div>
                )}

                <div className="info_item">
                    <DeliveryIcon />
                    <div className="info_content">
                        <span className="info_label">{t('delivery')}</span>
                        <span className={`info_value ${can_deliver ? 'available' : 'not_available'}`}>
                            {can_deliver ? t('deliveryAvailable') : t('deliveryNotAvailable')}
                        </span>
                    </div>
                </div>
            </div>

            {/* PRICE */}
            <div className="announcement_price">
                <span>{formatPrice(price, price_type, is_negotiable, barter)}</span>
                {price_min && price_min > 0 && (
                    <span className="price_min_note">
                        {t('minOrderPrice')}: {price_min.toLocaleString()} {currency}
                    </span>
                )}
            </div>

            {/* BUY BUTTON */}
            <button className="marketplace_buy_button" onClick={handleBuyClick}>
                <span>{t('buy')}</span>
                <QRIcon />
            </button>

            <p className="buy_note">{t('buyViaMobileApp')}</p>

            {/* RATING */}
            <div className="announcement_rating">
                <div className="rating_number">
                    <img src={starIcon} alt="Star Icon" />
                    <p>
                        {t('rating')} {String(rating || '5').slice(0, 3)}
                    </p>
                </div>
                <div className="announcement_reviews">
                    <p>
                        <span>{t('feedbacks')}:</span>
                        <span className="announcement_reviews_count">
                            {reviews || 0}
                        </span>
                    </p>
                </div>
            </div>

            {/* QR MODAL */}
            {showQRModal && (
                <div className="qr_modal_overlay" onClick={handleCloseQRModal}>
                    <div className="qr_modal" onClick={(e) => e.stopPropagation()}>
                        <button className="qr_modal_close" onClick={handleCloseQRModal}>×</button>
                        <h3>{t('downloadAppToBuy')}</h3>
                        <p>{t('scanQRToDownload')}</p>

                        <div className="qr_code_placeholder">
                            <div className="qr_code">
                                <svg viewBox="0 0 100 100" className="qr_svg">
                                    <rect x="10" y="10" width="25" height="25" fill="#000"/>
                                    <rect x="65" y="10" width="25" height="25" fill="#000"/>
                                    <rect x="10" y="65" width="25" height="25" fill="#000"/>
                                    <rect x="20" y="20" width="5" height="5" fill="#fff"/>
                                    <rect x="75" y="20" width="5" height="5" fill="#fff"/>
                                    <rect x="20" y="75" width="5" height="5" fill="#fff"/>
                                    <rect x="40" y="10" width="5" height="5" fill="#000"/>
                                    <rect x="50" y="10" width="5" height="5" fill="#000"/>
                                    <rect x="40" y="20" width="5" height="5" fill="#000"/>
                                    <rect x="45" y="25" width="5" height="5" fill="#000"/>
                                    <rect x="55" y="15" width="5" height="5" fill="#000"/>
                                    <rect x="10" y="40" width="5" height="5" fill="#000"/>
                                    <rect x="25" y="45" width="5" height="5" fill="#000"/>
                                    <rect x="15" y="55" width="5" height="5" fill="#000"/>
                                    <rect x="40" y="40" width="20" height="20" fill="#000"/>
                                    <rect x="45" y="45" width="10" height="10" fill="#fff"/>
                                    <rect x="65" y="40" width="5" height="5" fill="#000"/>
                                    <rect x="75" y="50" width="5" height="5" fill="#000"/>
                                    <rect x="85" y="45" width="5" height="5" fill="#000"/>
                                    <rect x="65" y="65" width="5" height="5" fill="#000"/>
                                    <rect x="70" y="70" width="5" height="5" fill="#000"/>
                                    <rect x="80" y="75" width="5" height="5" fill="#000"/>
                                    <rect x="75" y="80" width="5" height="5" fill="#000"/>
                                    <rect x="85" y="85" width="5" height="5" fill="#000"/>
                                    <rect x="40" y="65" width="5" height="5" fill="#000"/>
                                    <rect x="50" y="70" width="5" height="5" fill="#000"/>
                                    <rect x="45" y="80" width="5" height="5" fill="#000"/>
                                    <rect x="55" y="85" width="5" height="5" fill="#000"/>
                                </svg>
                            </div>
                        </div>

                        <div className="store_buttons">
                            <a href={appStoreUrl} target="_blank" rel="noopener noreferrer" className="store_button app_store">
                                App Store
                            </a>
                            <a href={playStoreUrl} target="_blank" rel="noopener noreferrer" className="store_button play_store">
                                Google Play
                            </a>
                        </div>

                        <div className="deep_link_section">
                            <p>{t('orOpenInApp')}</p>
                            <a href={deepLink} className="open_app_link">
                                {t('openInApp')}
                            </a>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default MarketplaceDescription;